// Modern JSX transform - React import not needed
import { memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { MapPinIcon } from '@heroicons/react/24/outline'
import SearchLocationInput from './SearchLocationInput'
import EntitySearchInput from './EntitySearchInput'
import LocationNotification from './LocationNotification'

/**
 * MapSearchPanel - Search controls for map pages
 * Entity-first layout: name search → location row → filter chips → sort → spot CTA
 */
const MapSearchPanel = memo(function MapSearchPanel({
  // Location search
  searchLocation,
  onSearchLocationChange,
  radius,
  onRadiusChange,
  onGeocode,
  onCurrentLocation,
  onSpotClick,
  isGeocoding,
  locationSource,

  // Entity search
  entitySearchQuery,
  onEntitySearchChange,
  entitySearchInputRef,
  isSearching,
  searchResults,
  showDropdown,
  onSelectEntity,
  onCloseDropdown,

  // Location info
  userLocation,
  searchLocationName,
  entityCount,
  isLoading,

  // Inline filter chips
  activeFilters = [],
  onFilterToggle = null,
  openNowActive = false,
  onToggleOpenNow = null,
  showEvents = true,
  onToggleShowEvents = null,

  // Optional features
  size = 'compact',
  sortBy = 'recent',
  onSortByChange = null,

  // Kept for compatibility
  onShare,
}) {
  const { t } = useTranslation()

  const handleClearLocation = useCallback(() => {
    if (onSearchLocationChange) onSearchLocationChange('')
  }, [onSearchLocationChange])

  const filterItems = [
    { key: 'unconfirmed', color: 'bg-[#D4A080]',  label: t('map.legend.unconfirmedShort', 'No conf.') },
    { key: 'one',         color: 'bg-primary/50', label: t('map.legend.oneConfirmationShort', '1 conf.') },
    { key: 'twoThree',   color: 'bg-accent',      label: t('map.legend.twoThreeConfirmationsShort', '2-3') },
    { key: 'fourPlus',   color: 'bg-primary',     label: t('map.legend.fourPlusConfirmationsShort', '4+') },
  ]

  const showFilterChips = onToggleOpenNow || onFilterToggle || onToggleShowEvents

  return (
    <div className="space-y-2">
      {/* 1. Entity name search — primary action */}
      <EntitySearchInput
        value={entitySearchQuery}
        onChange={onEntitySearchChange}
        inputRef={entitySearchInputRef}
        isSearching={isSearching}
        searchResults={searchResults}
        showDropdown={showDropdown}
        onSelectEntity={onSelectEntity}
        onCloseDropdown={onCloseDropdown}
        userLocation={userLocation}
        searchLocation={searchLocation}
        searchLocationName={searchLocationName}
        radius={radius}
        onClearLocation={handleClearLocation}
        size={size}
      />

      {/* 2. Location row — compact single row */}
      <SearchLocationInput
        searchLocation={searchLocation}
        onSearchLocationChange={onSearchLocationChange}
        radius={radius}
        onRadiusChange={onRadiusChange}
        onGeocode={onGeocode}
        onCurrentLocation={onCurrentLocation}
        isGeocoding={isGeocoding}
        locationSource={locationSource}
      />

      {/* 2b. Entity count context — shows how many results match current location/radius */}
      {entityCount != null && entityCount > 0 && (
        <p className="text-[11px] text-gray-400 px-0.5">
          {t('search.entityCount', '{{count}} vendors in this area', { count: entityCount })}
        </p>
      )}

      {/* 3. Sort row */}
      {onSortByChange && (
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide flex-shrink-0">
            {t('search.sortLabel', 'Sort')}
          </span>
          <button
            type="button"
            onClick={() => onSortByChange('recent')}
            onTouchEnd={(e) => { e.preventDefault(); onSortByChange('recent') }}
            className={`flex-shrink-0 text-xs px-3 py-1 rounded-full border font-medium transition-colors ${
              sortBy !== 'confirmations'
                ? 'bg-primary text-white border-primary shadow-sm'
                : 'bg-white text-gray-500 border-gray-200 hover:border-primary/40'
            }`}
          >
            {t('search.sortByRecent', 'Recent')}
          </button>
          <button
            type="button"
            onClick={() => onSortByChange('confirmations')}
            onTouchEnd={(e) => { e.preventDefault(); onSortByChange('confirmations') }}
            className={`flex-shrink-0 text-xs px-3 py-1 rounded-full border font-medium transition-colors ${
              sortBy === 'confirmations'
                ? 'bg-primary text-white border-primary shadow-sm'
                : 'bg-white text-gray-500 border-gray-200 hover:border-primary/40'
            }`}
          >
            {t('search.sortByConfirmations', 'Most confirmed')}
          </button>
        </div>
      )}

      {/* 4. Filter chips — two rows: status filters + extras */}
      {showFilterChips && (
        <div className="space-y-1.5">
          {/* Confirmation-level filter chips */}
          {onFilterToggle && (
            <div>
              <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide mb-1 block">
                {t('search.filterLabel', 'Filter')}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {filterItems.map(item => {
                  const isActive = activeFilters.length === 0 || activeFilters.includes(item.key)
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => onFilterToggle(item.key)}
                      onTouchEnd={(e) => { e.preventDefault(); onFilterToggle(item.key) }}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                        isActive
                          ? 'bg-white text-gray-700 border-gray-300 shadow-sm'
                          : 'bg-gray-50 text-gray-400 border-gray-200 opacity-50'
                      }`}
                    >
                      <span className={`w-2 h-2 ${item.color} rounded-full flex-shrink-0`} />
                      {item.label}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
          {/* Open Now + Events toggles */}
          {(onToggleOpenNow || onToggleShowEvents) && (
            <div className="flex flex-wrap gap-1.5">
              {onToggleOpenNow && (
                <button
                  type="button"
                  onClick={onToggleOpenNow}
                  onTouchEnd={(e) => { e.preventDefault(); onToggleOpenNow() }}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                    openNowActive
                      ? 'bg-green-500 text-white border-green-500 shadow-sm'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-green-400'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${openNowActive ? 'bg-white' : 'bg-green-400'}`} />
                  {t('map.openNow', 'Open Now')}
                </button>
              )}
              {onToggleShowEvents && (
                <button
                  type="button"
                  onClick={onToggleShowEvents}
                  onTouchEnd={(e) => { e.preventDefault(); onToggleShowEvents() }}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                    showEvents
                      ? 'bg-white text-gray-700 border-gray-300 shadow-sm'
                      : 'bg-gray-50 text-gray-400 border-gray-200 opacity-50'
                  }`}
                >
                  {t('map.events.label', 'Events')}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* 5. Spot CTA — prominent pill button */}
      {onSpotClick && (
        <button
          type="button"
          onClick={onSpotClick}
          onTouchEnd={(e) => { e.preventDefault(); onSpotClick() }}
          className="w-full flex items-center justify-center gap-1.5 bg-primary text-white text-sm font-semibold py-2 rounded-full shadow-sm hover:bg-primary/90 active:scale-[0.98] transition-all"
        >
          <MapPinIcon className="w-4 h-4 flex-shrink-0" />
          {t('map.spotSomething', 'Spot a vendor')}
        </button>
      )}

      {/* Location notification — non-compact mode only */}
      {size !== 'compact' && (
        <LocationNotification
          userLocation={userLocation}
          searchLocationName={searchLocationName}
          radius={radius}
          entityCount={entityCount}
          isLoading={isLoading}
          size={size}
        />
      )}
    </div>
  )
})

export default MapSearchPanel
